import { generateId } from '@freelancer/datastore/testing/helpers';
import type { ContestRubricItemScoreTypeApi } from 'api-typings/contests/contests';
import type {
  ContestRubric,
  ContestRubricSection,
} from './contest-rubrics.model';

export interface GenerateContestRubricOptions {
  readonly contestId: number;
  readonly rubricId?: number;
  readonly title?: string;
  readonly sections: readonly {
    readonly title: string;
    readonly description: string;
    readonly criteria: readonly {
      readonly id: number;
      readonly title: string;
      readonly description: string;
      readonly maxScore: number;
      readonly scoreType: ContestRubricItemScoreTypeApi;
      readonly commentRequired: boolean;
    }[];
  }[];
  readonly timeStarted?: number;
  readonly timeEnded?: number;
}

/**
 * Generates a ContestRubric document within the datastore.
 *
 * @param options GenerateContestRubricOptions
 * @returns ContestRubric
 */
export function generateContestRubricObject({
  rubricId,
  contestId,
  sections,
  timeStarted,
  timeEnded,
}: GenerateContestRubricOptions): ContestRubric {
  const id = rubricId !== undefined ? rubricId : generateId();
  const transformedSections = sections.map(
    section =>
      ({
        id: generateId(),
        title: section.title,
        description: section.description,
        maxScore: section.criteria.reduce(
          (acc, criterion) => acc + criterion.maxScore,
          0,
        ),
        items: section.criteria.map(criterion => ({
          id: criterion.id ?? generateId(),
          title: criterion.title,
          description: criterion.description,
          maxScore: criterion.maxScore,
          scoreType: criterion.scoreType,
          commentRequired: criterion.commentRequired,
        })),
      }) satisfies ContestRubricSection,
  );
  return {
    id,
    contestId,
    sections: transformedSections,
    timeStarted,
    timeEnded,
  };
}
